import type { SingPitchId } from './clingCantaConfig';
import { SING_PITCH_BY_ID } from './clingCantaConfig';
import type { VoiceTessituraProfile } from './clingVoiceConfig';
import { foldMidiToSingRange, nearestSingPitchLoose } from './clingCantaPitch';

/** MIDI calibrado dentro del rango La3–Do5 (sin forzar octavas artificiales). */
export function gameMidiFromDetected(
  midi: number,
  hint?: number | null
): number {
  return foldMidiToSingRange(midi, hint);
}

const SMOOTH_ALPHA: Record<VoiceTessituraProfile['id'], number> = {
  ninos: 0.32,
  grave: 0.16,
  agudo: 0.28,
};

const MAX_STEP: Record<VoiceTessituraProfile['id'], number> = {
  ninos: 4,
  grave: 2.5,
  agudo: 3.5,
};

const OCTAVE_JUMP = 12;

export function smoothStableGameMidi(
  prev: number | null,
  next: number,
  profile: VoiceTessituraProfile
): number {
  if (prev == null) return next;

  const diff = next - prev;
  const alpha = SMOOTH_ALPHA[profile.id];

  if (Math.abs(diff) >= OCTAVE_JUMP) {
    return prev + Math.sign(diff) * Math.min(Math.abs(diff) * 0.06, MAX_STEP[profile.id]);
  }

  const blended = prev + diff * alpha;
  const step = blended - prev;
  if (Math.abs(step) > MAX_STEP[profile.id]) {
    return prev + Math.sign(step) * MAX_STEP[profile.id];
  }
  return blended;
}

export interface PitchIdHoldState {
  locked: SingPitchId | null;
  candidate: SingPitchId | null;
  candidateFrames: number;
}

const HOLD_FRAMES: Record<VoiceTessituraProfile['id'], number> = {
  ninos: 2,
  grave: 4,
  agudo: 3,
};

/** Si el tono se alejó mucho de la nota bloqueada, liberar el bloqueo. */
const LOCK_RELEASE_SEMITONES = 2.5;

export function stablePitchId(
  midi: number,
  allowed: SingPitchId[],
  profile: VoiceTessituraProfile,
  hold: PitchIdHoldState
): { pitchId: SingPitchId | null; hold: PitchIdHoldState } {
  let holdState = hold;
  if (holdState.locked && SING_PITCH_BY_ID[holdState.locked]) {
    const lockedMidi = SING_PITCH_BY_ID[holdState.locked].midi;
    if (Math.abs(midi - lockedMidi) > LOCK_RELEASE_SEMITONES) {
      holdState = { locked: null, candidate: null, candidateFrames: 0 };
    }
  }

  const candidate = nearestSingPitchLoose(midi, allowed);
  if (!candidate) {
    return {
      pitchId: holdState.locked,
      hold: { locked: holdState.locked, candidate: null, candidateFrames: 0 },
    };
  }

  const candidateMidi = SING_PITCH_BY_ID[candidate].midi;
  if (Math.abs(midi - candidateMidi) > 1.35) {
    return {
      pitchId: null,
      hold: { locked: null, candidate, candidateFrames: 0 },
    };
  }

  let candidateFrames = holdState.candidateFrames;
  if (candidate === holdState.candidate) {
    candidateFrames += 1;
  } else {
    candidateFrames = 1;
  }

  const need = HOLD_FRAMES[profile.id];
  let locked = holdState.locked;
  if (locked == null || candidateFrames >= need) {
    locked = candidate;
  }

  return {
    pitchId: locked,
    hold: { locked, candidate, candidateFrames },
  };
}
